import React from 'react';
import { Link } from 'wouter';
import { CalendarDays, MapPin, Plane, User } from 'lucide-react';

/** A card under an answer (see AnswerCard in threads-agent.service.ts); built from the live record. */
export type AgentCard =
  | { kind: 'trip'; id: number; name: string; stage: string | null; startDate: string | null; endDate: string | null; href: string }
  | { kind: 'client'; id: number; name: string; location: string | null; href: string };

/** Only well-formed cards with in-app links; anything else is dropped. */
export function normalizeCards(raw: unknown): AgentCard[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c: any) =>
      c &&
      (c.kind === 'trip' || c.kind === 'client') &&
      Number.isInteger(c.id) &&
      typeof c.name === 'string' &&
      typeof c.href === 'string' &&
      /^\/(trips|clients)\/\d+$/.test(c.href),
  );
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function CardShell({ href, icon, title, children }: { href: string; icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="flex min-w-0 items-start gap-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm no-underline transition-colors hover:border-orange-300 hover:bg-orange-50 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:border-orange-700 dark:hover:bg-orange-900/20"
    >
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block truncate font-medium text-gray-900 dark:text-gray-100">{title}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">{children}</span>
      </span>
    </Link>
  );
}

/** Trips and clients the answer is about, linked to their pages. */
export function AnswerCards({ cards }: { cards: AgentCard[] }) {
  if (!cards.length) return null;
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-2" aria-label="Records in this answer">
      {cards.map((card) =>
        card.kind === 'trip' ? (
          <CardShell key={`trip-${card.id}`} href={card.href} icon={<Plane className="h-3.5 w-3.5" />} title={card.name}>
            {card.stage && <span className="capitalize">{card.stage.replace(/_/g, ' ')}</span>}
            {(card.startDate || card.endDate) && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {[formatDate(card.startDate), formatDate(card.endDate)].filter(Boolean).join(' – ')}
              </span>
            )}
          </CardShell>
        ) : (
          <CardShell key={`client-${card.id}`} href={card.href} icon={<User className="h-3.5 w-3.5" />} title={card.name}>
            <span>Client</span>
            {card.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {card.location}
              </span>
            )}
          </CardShell>
        ),
      )}
    </div>
  );
}
